import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { ConversationCategory, SupportAttachment } from '../../../common/models/support.model';
import { CONVERSATION_CATEGORIES } from '../../../common/models/support.model';
import { CATEGORY_META } from '../support-meta';
import { Composer } from '../components/Composer';

/** Write to support: what it's about, an optional subject, the message. */
export function NewConversation({
  initialCategory,
  upload,
  onSubmit,
  pageLabel,
  draft,
  onDraftChange,
}: {
  initialCategory?: ConversationCategory;
  upload: (file: File, onProgress: (f: number) => void) => Promise<SupportAttachment>;
  onSubmit: (payload: { category: ConversationCategory; subject: string; body: string; attachments: SupportAttachment[] }) => Promise<void>;
  pageLabel: string;
  draft?: string;
  onDraftChange?: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [category, setCategory] = useState<ConversationCategory>(initialCategory || 'question');
  const [subject, setSubject] = useState('');

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground">{t('SUPPORT.NEW.ABOUT')}</span>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('SUPPORT.NEW.ABOUT')}>
          {CONVERSATION_CATEGORIES.map((c) => {
            const meta = CATEGORY_META[c];
            const Icon = meta.icon;
            const active = category === c;
            return (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setCategory(c)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors',
                  active ? 'border-foreground ring-[0.5px] ring-foreground' : 'text-muted-foreground hover:bg-muted/60',
                )}
              >
                <Icon className="size-3.5" />
                {t(meta.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">{t('SUPPORT.NEW.SUBJECT')}</span>
        <input
          value={subject}
          maxLength={140}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={t('SUPPORT.NEW.SUBJECT_PLACEHOLDER')}
          className="h-9 rounded-xl border bg-card px-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground/30"
        />
      </label>

      <div className="mt-auto flex flex-col gap-2">
        <Composer
          autoFocus
          upload={upload}
          placeholder={t(category === 'bug' ? 'SUPPORT.NEW.PLACEHOLDER_BUG' : category === 'feature' ? 'SUPPORT.NEW.PLACEHOLDER_FEATURE' : 'SUPPORT.NEW.PLACEHOLDER')}
          initialText={draft}
          onDraftChange={onDraftChange}
          onSend={({ body, attachments }) => onSubmit({ category, subject, body, attachments })}
        />
        <p className="flex items-center gap-1.5 px-1 text-[11px] text-muted-foreground">
          <MapPin className="size-3 shrink-0" />
          <span className="truncate">{t('SUPPORT.NEW.CONTEXT_NOTE', { page: pageLabel })}</span>
        </p>
      </div>
    </div>
  );
}
